import { useState } from "react";
import { differenceInCalendarDays } from "date-fns";
import { User } from "features/user/types";
import {
  getDaysLeftInTrial,
  getHasPaymentMethod,
  getTrialEndDate,
} from "utils/reverse-trial";
import { resolveChargebeeSite } from "utils/chargebee-site";

/**
 * "reverse-trial" is the new full-featured no-credit-card trial. Per product
 * direction all in_trial users are treated as reverse-trial — the legacy
 * credit-card-required trial is no longer created for new signups.
 */
export type BannerType = "cancellation" | "reverse-trial" | null;

export function useSubscriptionBannerState(user: User | null) {
  const [isDismissed, setIsDismissed] = useState(() => {
    return sessionStorage.getItem("subscriptionBannerDismissed") === "true";
  });

  const dismissBanner = () => {
    sessionStorage.setItem("subscriptionBannerDismissed", "true");
    setIsDismissed(true);
  };

  if (!user) {
    return {
      shouldShowBanner: false,
      bannerType: null as BannerType,
      isDismissed: true,
      dismissBanner: () => {},
      daysUntilCancellation: null,
      cancellationDate: null,
      daysLeftInTrial: null,
      trialEndDate: null,
      hasPaymentMethod: false,
      isAdmin: false,
      adminEmail: undefined,
      gateway: resolveChargebeeSite(),
    };
  }

  const subscription = user.subscription?.subscription;
  const isAdmin = user.role === "admin";
  const adminEmail = user.adminEmail;
  const gateway = resolveChargebeeSite();

  // Priority 1: Skip if overdue invoices (DueInvoicesBanner handles this)
  const dueInvoicesCount = subscription?.due_invoices_count || 0;
  if (dueInvoicesCount > 0) {
    return {
      shouldShowBanner: false,
      bannerType: null as BannerType,
      isDismissed: true,
      dismissBanner,
      daysUntilCancellation: null,
      cancellationDate: null,
      daysLeftInTrial: null,
      trialEndDate: null,
      hasPaymentMethod: false,
      isAdmin,
      adminEmail,
      gateway,
    };
  }

  // Payment method detection (centralised helper)
  const hasPaymentMethod = getHasPaymentMethod(user);

  // Priority 2: Pending cancellation
  const isPendingCancellation =
    !!subscription?.cancelled_at &&
    (subscription.status === "in_trial" ||
      subscription.status === "non_renewing");

  let cancellationDate: Date | null = null;
  let daysUntilCancellation: number | null = null;

  if (isPendingCancellation) {
    // Use scheduled_cancellation_date if available, otherwise cancelled_at
    const cancelTimestamp =
      subscription.scheduled_cancellation_date || subscription.cancelled_at;
    if (cancelTimestamp) {
      cancellationDate = new Date(cancelTimestamp * 1000);
      daysUntilCancellation = differenceInCalendarDays(
        cancellationDate,
        new Date()
      );
    }
  }

  // Priority 3: Reverse trial (status === "in_trial" with no pending cancel).
  // Delegate to the shared reverse-trial helpers — they guard against
  // Chargebee's zero/NaN timestamps and fall back from `trial_end` to
  // `next_billing_at` consistently with `getTrialEndDate`'s contract, so a
  // malformed record can never surface as "NaN days left" copy. The helpers
  // return null when the subscription is not in_trial, so we don't need an
  // outer gate.
  const isInTrial = subscription?.status === "in_trial";
  const trialEndDate = getTrialEndDate(user);
  const daysLeftInTrial = getDaysLeftInTrial(user);

  // Determine banner type and whether to show
  let bannerType: BannerType = null;
  let shouldShowBanner = false;

  if (isPendingCancellation) {
    // Show pending cancellation to everyone (admins AND collaborators)
    bannerType = "cancellation";
    shouldShowBanner = true;
  } else if (isInTrial) {
    // Reverse trial banner:
    //  - Admins always see it (single "Pick a plan" CTA)
    //  - Collaborators see a passive variant that tells them to ask their admin
    bannerType = "reverse-trial";
    shouldShowBanner = true;
  }

  return {
    shouldShowBanner,
    bannerType,
    isDismissed,
    dismissBanner,
    daysUntilCancellation,
    cancellationDate,
    daysLeftInTrial,
    trialEndDate,
    hasPaymentMethod,
    isAdmin,
    adminEmail,
    gateway,
  };
}
